import React, { useState } from "react";
import { Menu, MenuItem, IconButton } from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { saveAs } from "file-saver";
import { materialsymbolsdownload, wg_report_logo } from "images";
import ExcelJS from "exceljs";
import {
  getProfileInfo,
  getEnergyUsage,
} from "selectors/energyAssistance.selectors";
import { useSelector } from "react-redux";
import { initialState } from "container/energyAssistance/store/initialState";

const EnergyUsageDownload = () => {
  const profileInfo = useSelector(getProfileInfo);
  const { AccountNumber, CustomerName } =
    profileInfo?.Data || initialState.profileInfo.Data;
  const energyUsage = useSelector(getEnergyUsage);

  const usageData = energyUsage?.Data?.EnergyUsageDetails_Res || [];

  const totalTherm = usageData
    .reduce((sum, item) => sum + item.Therms, 0)
    .toFixed(1);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Energy Usage");

    // Load logo
    const response = await fetch(wg_report_logo);
    const imageBlob = await response.blob();
    const base64 = await convertToBase64(imageBlob);
    const imageId = workbook.addImage({
      base64: base64,
      extension: "png",
    });

    // Metadata values
    const reportTitle = "Energy Usage";

    const metadataRows = [
      [reportTitle],
      ["Account Number:", AccountNumber],
      ["Customer Name:", CustomerName],
    ];

    // Add first row with report name and logo
    const row = worksheet.addRow([
      metadataRows[0][0],
      metadataRows[0][1],
      "",
      "",
      "",
      "",
    ]);
    row.getCell(1).alignment = { horizontal: "left" };
    row.getCell(6).alignment = { horizontal: "right" };
    worksheet.getRow(1).height = 60;

    // Add the logo to the last column (vertically aligned within the last cell of the table)
    worksheet.addImage(imageId, {
      tl: { col: 4, row: 0 },
      ext: { width: 170, height: 60 },
    });

    // Add other metadata rows
    metadataRows.slice(1).forEach(([label, value], index) => {
      const metadataRow = worksheet.addRow(value ? [label, value] : [label]);

      if (index === 0) {
        // Title of Report
        worksheet.mergeCells(`A${row.number}:B${row.number}`);
        row.getCell(1).font = {
          bold: true,
          size: 16,
        };
        row.getCell(1).alignment = {
          horizontal: "center",
          vertical: "middle",
        };
        row.height = 25;
      } else {
        metadataRow.eachCell((cell) => {
          cell.font = { bold: false, size: 12 };
          cell.alignment = { horizontal: "center" };
        });
      }
    });

    worksheet.addRow([]);

    worksheet.mergeCells("A5:C5"); // "Month" spans A5–C5
    worksheet.mergeCells("D5:F5"); // "Therms" spans D5–F5

    // Set header values
    worksheet.getCell("A5").value = "Month";
    worksheet.getCell("D5").value = "Therms";

    // Style the merged header cells
    ["A5", "D5"].forEach((cellRef) => {
      const cell = worksheet.getCell(cellRef);
      cell.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF0F3557" },
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    usageData.forEach((entry, index) => {
      const rowNumber = 6 + index;

      worksheet.mergeCells(`A${rowNumber}:C${rowNumber}`);
      worksheet.mergeCells(`D${rowNumber}:F${rowNumber}`);

      worksheet.getCell(`A${rowNumber}`).value = entry.MonthYear;
      worksheet.getCell(`D${rowNumber}`).value = entry.Therms;

      // Style the merged cells
      ["A", "D"].forEach((col) => {
        const cell = worksheet.getCell(`${col}${rowNumber}`);
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.font = { size: 12 };
      });
    });

    const totalRowNumber = worksheet.lastRow.number + 1; // location is below the last data

    worksheet.mergeCells(`A${totalRowNumber}:C${totalRowNumber}`);
    worksheet.mergeCells(`D${totalRowNumber}:F${totalRowNumber}`);

    worksheet.getCell(`A${totalRowNumber}`).value = "Total";
    worksheet.getCell(`D${totalRowNumber}`).value = totalTherm;

    // Total Therms
    ["A", "D"].forEach((col) => {
      const cell = worksheet.getCell(`${col}${totalRowNumber}`);
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF0F3557" },
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const value = cell.value ? cell.value.toString() : "";
        maxLength = Math.max(maxLength, value.length);
      });
      column.width = maxLength + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "Energy_Assistance_Energy_Usage_Details.xlsx");
  };

  const convertToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const downloadPDF = () => {
    const doc = new jsPDF("portrait");

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const imgWidth = 60;
    const imgHeight = 20;

    const startY = 20; // Starting Y position

    // Report Information - LEFT aligned
    doc.setFontSize(16);
    doc.text("Energy Usage", margin, startY);

    doc.setFontSize(12);
    doc.text(`Account Number: ${AccountNumber}`, margin, startY + 10);
    doc.text(`Customer Name: ${CustomerName}`, margin, startY + 18);

    // Logo - RIGHT aligned
    doc.addImage(
      wg_report_logo,
      "PNG",
      pageWidth - imgWidth - margin,
      startY - 5,
      imgWidth,
      imgHeight
    );

    const headers = [["Month", "Usage"]];
    const body = usageData.map((row) => [row.MonthYear, row.Therms.toFixed(1)]);

    body.push([
      {
        content: "Total",
        styles: { fillColor: "#0F3557", textColor: "white", fontStyle: "bold" },
      },
      {
        content: totalTherm,
        styles: { fillColor: "#0F3557", textColor: "white", fontStyle: "bold" },
      },
    ]);

    doc.autoTable({
      startY: 56,
      head: headers,
      body: body,
      styles: {
        fontSize: 10,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: "#0F3557",
        textColor: "white",
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        valign: "middle",
        halign: "center",
      },
    });

    // Save PDF
    doc.save("Energy_Assistance_Energy_Usage_Details.pdf");
  };

  return (
    <>
      <IconButton
        size="small"
        color="primary"
        className="downloadenergy"
        onClick={handleClick}
        sx={{
          position: "relative !important",
          top: "-2px !important",
        }}
      >
        <img src={materialsymbolsdownload} alt="download"></img>
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            handleClose();
            downloadExcel();
          }}
        >
          Excel
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            downloadPDF();
          }}
        >
          PDF
        </MenuItem>
      </Menu>
    </>
  );
};

export default EnergyUsageDownload;
